import { Button } from '@askable/ui/core/button';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary } from 'react-error-boundary';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'src/lib/routing';

import { ErrorPage } from 'components/ErrorPage/ErrorPage';

import type { ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';

type ErrorFallbackProps = FallbackProps & {
  showChatOption: boolean;
};

const ErrorFallback = ({ resetErrorBoundary, showChatOption }: ErrorFallbackProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleGoBack = () => {
    resetErrorBoundary();
    navigate('/chat', { replace: true });
  };

  const handleRetry = () => {
    resetErrorBoundary();
  };

  return (
    <ErrorPage className="text flex w-80 flex-col items-center justify-center text-center md:max-w-[540px]">
      <h2 className="text-lg font-medium text-foreground">{t('sections.errorBoundary.chat.title')}</h2>
      {showChatOption ? (
        <p className="text-sm">{t('sections.errorBoundary.chat.default')}</p>
      ) : (
        <p className="text-sm">{t('sections.errorBoundary.chat.noChatId')}</p>
      )}
      <p className="text-sm">
        <Trans
          i18nKey="sections.errorBoundary.chat.contactSupport"
          components={{
            1: (
              // `Trans` fills the anchor from the translation string at runtime.
              // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans fills the anchor text at runtime
              <a
                href="https://help.askable.com/en/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-dark text-primary underline"
              />
            ),
          }}
        />
      </p>

      <div className="flex gap-2">
        <Button onClick={handleRetry} variant="primary">
          {t('sections.errorBoundary.chat.retryBtn', 'Retry')}
        </Button>
        {showChatOption && (
          <Button onClick={handleGoBack} variant="default">
            {t('sections.errorBoundary.chat.newChatBtn', 'Back to Chats')}
          </Button>
        )}
      </div>
    </ErrorPage>
  );
};

export const ErrorBoundaryChatRoute = ({
  children,
  showChatOption,
}: {
  children: ReactNode;
  showChatOption: boolean;
}) => {
  return (
    <ErrorBoundary
      fallbackRender={fallbackProps => (
        <ErrorFallback
          resetErrorBoundary={fallbackProps.resetErrorBoundary}
          error={fallbackProps.error}
          showChatOption={showChatOption}
        />
      )}
      onError={(error, errorInfo) => {
        // Log error to DataDog
        datadogRum.addError(error, {
          source: 'chat-route',
          type: 'error-boundary',
          errorInfo: errorInfo.componentStack,
        });
        datadogRum.startSessionReplayRecording();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
